import type { ReactNode } from 'react';

import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { Info } from 'lucide-react';

import type { GetOrganisationEngagementScoreQuery } from '@graphql/__generated__/graphql';
import { ScoreSignalRow } from '@modules/organisations/components/ScoreSignalRow';
import { useLocale } from '@shared/hooks/locale';

import { ENGAGEMENT_SIGNALS } from './engagementSignals';
import {
  type HealthTone,
  getHealthLabel,
  getHealthMainColor,
  getHealthTone,
} from './healthTone';

export type OrganisationEngagementScore = NonNullable<
  GetOrganisationEngagementScoreQuery['organisation_engagement_score']
>;

const Header = ({
  title,
  tooltip,
  action,
}: {
  title: string;
  tooltip: string;
  action?: ReactNode;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ justifyContent: 'space-between', alignItems: 'center' }}
  >
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Tooltip title={tooltip} arrow placement="top">
        <Info
          size={14}
          color="currentColor"
          style={{ opacity: 0.6, flexShrink: 0 }}
        />
      </Tooltip>
    </Stack>
    {action}
  </Stack>
);

const Gauge = ({ score, tone }: { score: number; tone: HealthTone }) => {
  const clamped = Math.min(Math.max(score, 0), 100);
  return (
    <Box
      sx={{
        position: 'relative',
        width: 88,
        height: 88,
        flexShrink: 0,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={88}
        thickness={5}
        sx={{ color: 'action.hover', position: 'absolute', inset: 0 }}
      />
      <CircularProgress
        variant="determinate"
        value={clamped}
        size={88}
        thickness={5}
        sx={{
          color: (theme: Theme) => getHealthMainColor(theme, tone),
          position: 'absolute',
          inset: 0,
          '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {`${score}%`}
        </Typography>
      </Box>
    </Box>
  );
};

const summaryChipSx = {
  height: 22,
  fontWeight: 600,
  bgcolor: 'background.paper',
  color: 'text.secondary',
  borderColor: 'divider',
  '& .MuiChip-label': {
    px: 0.75,
  },
};

type Props = {
  engagementScore: OrganisationEngagementScore | null;
  loading?: boolean;
  error?: boolean;
  formatDate: (date: string | null | undefined) => string | null;
};

export const EngagementScoreCard = ({
  engagementScore,
  loading = false,
  error = false,
  formatDate,
}: Props) => {
  const { t } = useLocale();

  const header = (
    <Header
      title={t('Engagement score')}
      tooltip={t(
        'Same score as in Close. Average of the counted signals, updated daily from the data warehouse.',
      )}
      action={
        <Chip
          size="small"
          label={t('Last 30 days')}
          variant="outlined"
          sx={summaryChipSx}
        />
      }
    />
  );

  if (loading) {
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {header}
            <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
              <Skeleton
                variant="circular"
                sx={{ width: 88, height: 88, flexShrink: 0 }}
              />
              <Skeleton width={120} height={48} />
            </Stack>
            <Stack spacing={0.5}>
              {ENGAGEMENT_SIGNALS.map(signal => (
                <ScoreSignalRow
                  key={signal.key}
                  label=""
                  tone="neutral"
                  percent={0}
                  value=""
                  loading
                />
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (engagementScore == null) {
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {header}
            <Typography variant="body2" color="text.secondary">
              {error
                ? t('Could not load engagement score.')
                : t('No engagement score: only Close customers are scored.')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const { score, previous_score: previousScore } = engagementScore;
  const tone = getHealthTone(score);
  const delta = score - previousScore;
  const signalsByKey = new Map(
    engagementScore.signals.map(signal => [signal.key, signal]),
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {header}
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
            <Gauge score={score} tone={tone} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: (theme: Theme) => getHealthMainColor(theme, tone),
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {getHealthLabel(score, t)}
              </Typography>
              <Typography
                variant="caption"
                color={
                  delta > 0
                    ? 'success.main'
                    : delta < 0
                    ? 'error.main'
                    : 'text.secondary'
                }
                sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}
              >
                {t('{{delta}} vs previous 30 days', {
                  delta: delta > 0 ? `+${delta}` : delta < 0 ? delta : '±0',
                })}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {t('Updated {{date}}', {
                  date: formatDate(engagementScore.updated_at),
                })}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={0.5}>
            {ENGAGEMENT_SIGNALS.map(({ key, label }) => {
              const signal = signalsByKey.get(key);
              if (signal == null) {
                return null;
              }
              return (
                <ScoreSignalRow
                  key={key}
                  label={label(t)}
                  tone={
                    signal.counted ? getHealthTone(signal.score) : 'neutral'
                  }
                  percent={signal.score}
                  value={signal.counted ? `${signal.score}%` : '—'}
                  eligible={signal.counted}
                  tooltip={
                    signal.counted ? undefined : t('Not counted in the score')
                  }
                />
              );
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
